import React, { useEffect, useState } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip
} from '@material-ui/core';
import * as templateServices from '../../services/template.services';
import './desktopTemplates.css';
import Header from '../../components/header';

function DesktopTemplatesPage() {
  const [templates, setTemplates] = useState([]);
  const [activeTemplate, setActiveTemplate] = useState(null);
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadVersion, setUploadVersion] = useState('');
  const [uploadDescription, setUploadDescription] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadTemplates();
  }, []);

  const loadTemplates = () => {
    templateServices.getAllTemplates()
      .then((res) => {
        console.log('Templates:', res);
        setTemplates(res.data || []);

        // Find active template
        const active = (res.data || []).find(t => t.isActive);
        setActiveTemplate(active);
      })
      .catch((err) => {
        console.error('Error loading templates:', err);
        alert('Failed to load templates');
      });
  };

  const handleUpload = () => {
    if (!uploadFile) {
      alert('Please select a file');
      return;
    }

    if (!uploadVersion) {
      alert('Please enter a version number');
      return;
    }

    setLoading(true);
    templateServices.uploadTemplate(uploadFile, uploadVersion, uploadDescription, localStorage.getItem('userEmail') || 'admin')
      .then((res) => {
        console.log('Upload success:', res);
        alert('Template uploaded successfully!');
        setUploadModalOpen(false);
        resetUploadForm();
        loadTemplates();
      })
      .catch((err) => {
        console.error('Upload error:', err);
        alert('Failed to upload template. Please try again.');
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleSetActive = (templateId) => {
    if (!window.confirm('Are you sure you want to set this template as active? This will be used for all new desktop builds.')) {
      return;
    }

    setLoading(true);
    templateServices.setActiveTemplate(templateId)
      .then((res) => {
        console.log('Set active success:', res);
        alert('Template activated successfully!');
        loadTemplates();
      })
      .catch((err) => {
        console.error('Set active error:', err);
        alert('Failed to activate template');
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleDelete = (templateId, isActive) => {
    if (isActive) {
      alert('Cannot delete the active template. Please activate another template first.');
      return;
    }

    if (!window.confirm('Are you sure you want to delete this template? This action cannot be undone.')) {
      return;
    }

    setLoading(true);
    templateServices.deleteTemplate(templateId)
      .then((res) => {
        console.log('Delete success:', res);
        alert('Template deleted successfully!');
        loadTemplates();
      })
      .catch((err) => {
        console.error('Delete error:', err);
        alert('Failed to delete template');
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];

    if (!file) return;

    // Validate file type
    if (!file.name.endsWith('.zip')) {
      alert('Please select a .zip file');
      e.target.value = null;
      return;
    }

    // Validate file size (max 500MB)
    const maxSize = 500 * 1024 * 1024;
    if (file.size > maxSize) {
      alert('File size exceeds 500MB limit');
      e.target.value = null;
      return;
    }

    setUploadFile(file);
  };

  const resetUploadForm = () => {
    setUploadFile(null);
    setUploadVersion('');
    setUploadDescription('');
  };

  const handleCloseUploadModal = () => {
    setUploadModalOpen(false);
    resetUploadForm();
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
  };

  return (
    <>
      <Header />
      <div className="admin-page">
        <div className='page-head'>
          <h1 className="header">Desktop Templates</h1>
          <div className='template-actions'>
            <Button className="add-button" onClick={() => setUploadModalOpen(true)} disabled={loading}>
              Upload New Template
            </Button>
          </div>
        </div>

        {activeTemplate && (
          <div className="active-template-info">
            <h3>Active Template: {activeTemplate.version}</h3>
            <p>S3 Location: {activeTemplate.s3Key}</p>
            <p>Size: {formatFileSize(activeTemplate.fileSize)}</p>
            <p>Uploaded: {formatDate(activeTemplate.uploadedAt)}</p>
          </div>
        )}

        <TableContainer className="admin-table-container" component={Paper}>
          <Dialog
            open={uploadModalOpen}
            onClose={handleCloseUploadModal}
            aria-labelledby="form-dialog-title"
            maxWidth="sm"
            fullWidth
          >
            <DialogTitle id="form-dialog-title">Upload Desktop Template</DialogTitle>
            <DialogContent>
              <label className="edit-book-label">Version *</label>
              <input
                autoFocus
                type="text"
                placeholder="e.g., 1.0.0"
                value={uploadVersion}
                onChange={(e) => setUploadVersion(e.target.value)}
                className="edit-book-name"
                disabled={loading}
              />

              <label className="edit-book-label">Description</label>
              <textarea
                type="text"
                placeholder="What's new in this version..."
                value={uploadDescription}
                onChange={(e) => setUploadDescription(e.target.value)}
                className="edit-book-description"
                rows={3}
                disabled={loading}
              />

              <label className="voice-file-label">Template File (ZIP) *</label>
              <input
                type="file"
                accept=".zip"
                className='voice-file-uploader'
                onChange={handleFileSelect}
                disabled={loading}
              />
              <p className='instructions'>
                Upload a zipped desktop electron template (max 500MB).
                This should contain the complete electron app structure.
              </p>

              {uploadFile && (
                <p className='file-info'>
                  Selected: {uploadFile.name} ({formatFileSize(uploadFile.size)})
                </p>
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={handleCloseUploadModal} color="primary" disabled={loading}>
                Cancel
              </Button>
              <Button onClick={handleUpload} color="primary" disabled={loading || !uploadFile || !uploadVersion}>
                {loading ? 'Uploading...' : 'Upload'}
              </Button>
            </DialogActions>
          </Dialog>

          <Table className="admin-table">
            <TableHead>
              <TableRow className="table-header-row">
                <TableCell className="table-cell">Version</TableCell>
                <TableCell className="table-cell">Description</TableCell>
                <TableCell className="table-cell">File Size</TableCell>
                <TableCell className="table-cell">Uploaded By</TableCell>
                <TableCell className="table-cell">Uploaded At</TableCell>
                <TableCell className="table-cell">Status</TableCell>
                <TableCell className="table-cell">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {templates.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" className="table-cell">
                    No templates found. Upload your first template to get started.
                  </TableCell>
                </TableRow>
              ) : (
                templates.map((template) => (
                  <TableRow key={template._id} className="table-row">
                    <TableCell className="table-cell">
                      {template.version}
                    </TableCell>
                    <TableCell className="table-cell">
                      {template.description || 'No description'}
                    </TableCell>
                    <TableCell className="table-cell">
                      {formatFileSize(template.fileSize)}
                    </TableCell>
                    <TableCell className="table-cell">
                      {template.uploadedBy || 'N/A'}
                    </TableCell>
                    <TableCell className="table-cell">
                      {formatDate(template.uploadedAt)}
                    </TableCell>
                    <TableCell className="table-cell">
                      {template.isActive ? (
                        <Chip label="Active" color="primary" size="small" />
                      ) : (
                        <Chip label="Inactive" size="small" />
                      )}
                    </TableCell>
                    <TableCell className="table-cell">
                      {!template.isActive && (
                        <>
                          <Button
                            className="activate-button"
                            onClick={() => handleSetActive(template._id)}
                            disabled={loading}
                            size="small"
                          >
                            Set Active
                          </Button>
                          <Button
                            className="delete-button"
                            onClick={() => handleDelete(template._id, template.isActive)}
                            disabled={loading}
                            size="small"
                          >
                            Delete
                          </Button>
                        </>
                      )}
                      {template.isActive && (
                        <span className="active-label">Currently in use</span>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </div>
    </>
  );
}

export default DesktopTemplatesPage;